import { Dispatch } from 'redux';
import { StorageBlockPath, DashboardGridState, StorageBlocksPath, Layouts } from '@tradingblock/types';
import { DataState } from '../../state';
import { BlockActions, RootAction, UIActions } from '../../actions';
import { uiApiToken, dashboardIdSelector } from '../../dataSelectors';
import { UiApiClient } from '../../../../context/Storage';
import { Config } from '../../../../config';
import { applyDefaultSettings } from '../../utilities/blockStateUtilities';
import _ from 'lodash';

export const handleBlockDeletion = async (state: DataState, dispatch: Dispatch<RootAction>, blockId: string) => {
  const provider = UiApiClient(state, dispatch);
  if (provider) {
    await provider.delete(StorageBlockPath(blockId));
  }
};

export const handleDashboardBlocksRequest = async (state: DataState, dispatch: Dispatch<RootAction>) => {
  const dashboardId = dashboardIdSelector(state) || '';
  const provider = UiApiClient(state, dispatch);

  if (!provider) {
    dispatch(UIActions.endDashboardTransition());
    return;
  }

  try {
    const apiRes = await provider.get<DashboardGridState>(StorageBlocksPath(dashboardId));

    if (!apiRes || !apiRes.blocks) {
      dispatch(BlockActions.noBlocks());
      dispatch(UIActions.endDashboardTransition());
      return;
    }

    const blocks = apiRes.blocks || [];
    const groups = apiRes.groups || [];

    // Sync block symbols with groups, if mismatched
    const updatedBlocks = blocks.map((block: any) => {
      const group = groups.find((g: any) => g.groupId === block.groupId);
      return group && group.symbol !== block.symbol ? { ...block, symbol: group.symbol } : block;
    });

    // Apply default settings to blocks if not present
    const blocksWithDefaults = updatedBlocks.map((block: any) => {
      const settings = applyDefaultSettings(block.settings, block.type);
      return { ...block, settings };
    });

    // Create default layouts if none exist
    const defaultLayouts = {
      lg: blocks.map((block, i) => ({
        i: block.blockId,
        x: (i % 2) * 6,
        y: Math.floor(i / 2) * 4,
        w: 6,
        h: 4,
        type: block.type,
      })),
      md: blocks.map((block, i) => ({
        i: block.blockId,
        x: (i % 2) * 5,
        y: Math.floor(i / 2) * 4,
        w: 5,
        h: 4,
        type: block.type,
      })),
      sm: blocks.map((block, i) => ({
        i: block.blockId,
        x: 0,
        y: i * 4,
        w: 12,
        h: 4,
        type: block.type,
      })),
      xs: blocks.map((block, i) => ({
        i: block.blockId,
        x: 0,
        y: i * 4,
        w: 12,
        h: 4,
        type: block.type,
      })),
    };

    // Dispatch the updated blocks with layouts
    dispatch(
      BlockActions.receiveBlocks({
        ...apiRes,
        dashboardId,
        blocks: blocksWithDefaults,
        layouts:
          !_.get(apiRes, 'layouts.lg.length') && !_.get(apiRes, 'layouts.md.length')
            ? defaultLayouts
            : (apiRes.layouts as Layouts),
      })
    );

    dispatch(UIActions.endDashboardTransition());
  } catch (ex) {
    console.error('tryLoadBlocks error', ex);
    dispatch(UIActions.endDashboardTransition());
    return [];
  }
};
